'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { applyCnpjMask } from '@/shared/utils/cnpj';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { reportError } from '@/shared/utils/reportError';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { OBJETIVO_LABELS, PLANO_TIER_LABELS, PLANO_TIER_PRECOS_MENSAIS, WIZARD_STEP_LABELS, WIZARD_STEP_ORDER } from '../constants';
import type { AbortableThunk, ObjetivoSelf, PerfilSelf, PlanoPeriodo, PlanoTier, SelfStep, WizardStepView } from '../types';
import { adicionarMensagemUsuario, buscarContextoEmpresaSelf, carregarCamposObjetivoSelf, carregarObjetivosSelf, consultarCnpjNegociacaoSelf, consultarPagamentoSelf, criarAgenteSelf, dispensarRestauracaoSessao, editarDadosPerfil, enviarMensagemSimulacaoSelf, iniciarPagamentoSelf, marcarTentouBuscar, reiniciar, restaurarSessao, selectSelfBuscandoContexto, selectSelfBuscouContexto, selectSelfCamposError, selectSelfCamposObjetivo, selectSelfCamposObjetivoDe, selectSelfCarregandoCampos, selectSelfCarregandoObjetivos, selectSelfChatInput, selectSelfChatMensagens, selectSelfCnpjConsultado, selectSelfCnpjEncontrado, selectSelfCnpjError, selectSelfConsultandoCnpj, selectSelfContextoEncontrado, selectSelfContextoError, selectSelfCriandoAgente, selectSelfCriarAgenteError, selectSelfIniciandoPagamento, selectSelfInvoiceUrl, selectSelfLeadId, selectSelfPagamentoError, selectSelfLexiDigitando, selectSelfObjetivo, selectSelfObjetivosCatalogo, selectSelfObjetivosError, selectSelfPerfil, selectSelfPlanoPeriodo, selectSelfPlanoTier, selectSelfPollingPagamentoExpirado, selectSelfRestauracaoSessaoConcluida, selectSelfStep, selectSelfTentouBuscar, selectSelfTermosAceitos, selectSelfValoresParametros, setChatInput, setObjetivo, setPerfilCampo, setPlanoPeriodo, setPlanoTier, setStep, setTermosAceitos, setValorParametro } from '../store';
import { urlPagamentoSegura } from '@/shared/utils/pagamentoUrl';
import { objetivoPulaParametros, podeAvancarParametros } from '../utils/parametrosCampos';
import { lerSessaoSelf, limparSessaoSelf, limparSessaoSelfDaAba } from '../utils/sessaoSelf';
import { PARAM_ETAPA, resolverEtapa } from './useSelfStepUrl';
import { cnpjPerfilValido, emailPerfilValido, perfilProntoParaBusca } from '../utils/perfilValidacao';
const STEPS_QUE_EXIGEM_CONFIGURACAO: SelfStep[] = ['parametros', 'revisao', 'pagamento', 'waitingPayment', 'errorPayment'];
const STEPS_RESTAURAVEIS_POR_SESSAO: SelfStep[] = ['pagamento', 'waitingPayment', 'errorPayment'];
export const useNegociacaoSelf = () => {
  const $ = _c(187);
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const step = useAppSelector(selectSelfStep);
  const objetivo = useAppSelector(selectSelfObjetivo);
  const perfil = useAppSelector(selectSelfPerfil);
  const leadId = useAppSelector(selectSelfLeadId);
  const objetivosCatalogo = useAppSelector(selectSelfObjetivosCatalogo);
  const carregandoObjetivos = useAppSelector(selectSelfCarregandoObjetivos);
  const objetivosError = useAppSelector(selectSelfObjetivosError);
  const buscandoContexto = useAppSelector(selectSelfBuscandoContexto);
  const buscouContexto = useAppSelector(selectSelfBuscouContexto);
  const contextoEncontrado = useAppSelector(selectSelfContextoEncontrado);
  const tentouBuscar = useAppSelector(selectSelfTentouBuscar);
  const contextoError = useAppSelector(selectSelfContextoError);
  const consultandoCnpj = useAppSelector(selectSelfConsultandoCnpj);
  const cnpjConsultado = useAppSelector(selectSelfCnpjConsultado);
  const cnpjEncontrado = useAppSelector(selectSelfCnpjEncontrado);
  const cnpjError = useAppSelector(selectSelfCnpjError);
  const camposObjetivo = useAppSelector(selectSelfCamposObjetivo);
  const camposObjetivoDe = useAppSelector(selectSelfCamposObjetivoDe);
  const carregandoCampos = useAppSelector(selectSelfCarregandoCampos);
  const camposError = useAppSelector(selectSelfCamposError);
  const valoresParametros = useAppSelector(selectSelfValoresParametros);
  const criandoAgente = useAppSelector(selectSelfCriandoAgente);
  const criarAgenteError = useAppSelector(selectSelfCriarAgenteError);
  const planoPeriodo = useAppSelector(selectSelfPlanoPeriodo);
  const planoTier = useAppSelector(selectSelfPlanoTier);
  const termosAceitos = useAppSelector(selectSelfTermosAceitos);
  const chatMensagens = useAppSelector(selectSelfChatMensagens);
  const chatInput = useAppSelector(selectSelfChatInput);
  const lexiDigitando = useAppSelector(selectSelfLexiDigitando);
  const invoiceUrl = useAppSelector(selectSelfInvoiceUrl);
  const iniciandoPagamento = useAppSelector(selectSelfIniciandoPagamento);
  const pagamentoError = useAppSelector(selectSelfPagamentoError);
  const pollingPagamentoExpirado = useAppSelector(selectSelfPollingPagamentoExpirado);
  const restauracaoSessaoConcluida = useAppSelector(selectSelfRestauracaoSessaoConcluida);
  const criarAgentePromiseRef = useRef(null);
  const consultaCnpjPromiseRef = useRef(null);
  let t0;
  if ($[0] !== perfil.email) {
    t0 = emailPerfilValido(perfil.email);
    $[0] = perfil.email;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const emailValido = t0;
  let t1;
  if ($[2] !== perfil.nome || $[3] !== tentouBuscar) {
    t1 = tentouBuscar && perfil.nome.trim().length === 0;
    $[2] = perfil.nome;
    $[3] = tentouBuscar;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const showNomeError = t1;
  const showEmailError = tentouBuscar && !emailValido;
  let t2;
  if ($[5] !== perfil.cnpj || $[6] !== tentouBuscar) {
    t2 = tentouBuscar && !cnpjPerfilValido(perfil.cnpj);
    $[5] = perfil.cnpj;
    $[6] = tentouBuscar;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const showCnpjError = t2;
  let t3;
  if ($[8] !== perfil.razaoSocial || $[9] !== tentouBuscar) {
    t3 = tentouBuscar && perfil.razaoSocial.trim().length === 0;
    $[8] = perfil.razaoSocial;
    $[9] = tentouBuscar;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const showRazaoSocialError = t3;
  let t4;
  if ($[11] !== perfil.nomeFantasia || $[12] !== tentouBuscar) {
    t4 = tentouBuscar && perfil.nomeFantasia.trim().length === 0;
    $[11] = perfil.nomeFantasia;
    $[12] = tentouBuscar;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const showNomeFantasiaError = t4;
  let t5;
  if ($[14] !== perfil.site || $[15] !== tentouBuscar) {
    t5 = tentouBuscar && perfil.site.trim().length === 0;
    $[14] = perfil.site;
    $[15] = tentouBuscar;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const showSiteError = t5;
  let t6;
  if ($[17] !== perfil) {
    t6 = perfilProntoParaBusca(perfil);
    $[17] = perfil;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const podeBuscar = t6;
  let t7;
  if ($[19] !== buscouContexto || $[20] !== perfil.sobre) {
    t7 = buscouContexto && perfil.sobre.trim().length > 0;
    $[19] = buscouContexto;
    $[20] = perfil.sobre;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const podeAvancarPerfil = t7;
  let t8;
  if ($[22] !== camposObjetivo || $[23] !== valoresParametros) {
    t8 = podeAvancarParametros(camposObjetivo, valoresParametros);
    $[22] = camposObjetivo;
    $[23] = valoresParametros;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const podeAvancarParametrosStep = t8;
  const currentIndex = WIZARD_STEP_ORDER[step] ?? 0;
  let t9;
  if ($[25] !== currentIndex) {
    t9 = WIZARD_STEP_LABELS.map((label, index) => ({
      label,
      filled: index <= currentIndex,
      active: index === currentIndex
    }));
    $[25] = currentIndex;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const wizardSteps = t9;
  const objetivoLabel = objetivo ? OBJETIVO_LABELS[objetivo] : "\u2014";
  let t10;
  if ($[27] !== objetivo) {
    t10 = objetivoPulaParametros(objetivo);
    $[27] = objetivo;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const objetivoSemParametros = t10;
  const stepAnteriorRevisao = objetivoSemParametros ? "perfil" : "parametros";
  const planoValorMensal = PLANO_TIER_PRECOS_MENSAIS[planoTier];
  const cobrancaLabel = planoPeriodo === "anual" ? "cobran\xE7a anual" : "cobran\xE7a mensal";
  const planoResumoLabel = `Plano ${PLANO_TIER_LABELS[planoTier]} • R$ ${planoValorMensal}/mês · ${cobrancaLabel}`;
  let t11;
  let t12;
  if ($[29] !== carregandoObjetivos || $[30] !== dispatch || $[31] !== objetivosCatalogo.length || $[32] !== objetivosError || $[33] !== step) {
    t11 = () => {
      if (step !== "objetivo") {
        return;
      }
      if (objetivosCatalogo.length > 0 || carregandoObjetivos || objetivosError) {
        return;
      }
      dispatch(carregarObjetivosSelf());
    };
    t12 = [step, objetivosCatalogo.length, carregandoObjetivos, objetivosError, dispatch];
    $[29] = carregandoObjetivos;
    $[30] = dispatch;
    $[31] = objetivosCatalogo.length;
    $[32] = objetivosError;
    $[33] = step;
    $[34] = t11;
    $[35] = t12;
  } else {
    t11 = $[34];
    t12 = $[35];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[36] !== camposError || $[37] !== camposObjetivoDe || $[38] !== carregandoCampos || $[39] !== dispatch || $[40] !== objetivo || $[41] !== step) {
    t13 = () => {
      if (step !== "parametros" || !objetivo) {
        return;
      }
      if (camposObjetivoDe === objetivo || carregandoCampos || camposError) {
        return;
      }
      dispatch(carregarCamposObjetivoSelf(objetivo));
    };
    t14 = [step, objetivo, camposObjetivoDe, carregandoCampos, camposError, dispatch];
    $[36] = camposError;
    $[37] = camposObjetivoDe;
    $[38] = carregandoCampos;
    $[39] = dispatch;
    $[40] = objetivo;
    $[41] = step;
    $[42] = t13;
    $[43] = t14;
  } else {
    t13 = $[42];
    t14 = $[43];
  }
  useEffect(t13, t14);
  const restauracaoTentadaRef = useRef(false);
  let t15;
  let t16;
  if ($[44] !== dispatch || $[45] !== leadId || $[46] !== searchParams) {
    t15 = () => {
      if (restauracaoTentadaRef.current) {
        return;
      }
      restauracaoTentadaRef.current = true;
      const etapaAlvo = resolverEtapa(searchParams.get(PARAM_ETAPA));
      if (!leadId && STEPS_RESTAURAVEIS_POR_SESSAO.includes(etapaAlvo)) {
        const sessao = lerSessaoSelf();
        if (sessao) {
          dispatch(restaurarSessao(sessao));
          return;
        }
      }
      dispatch(dispensarRestauracaoSessao());
    };
    t16 = [leadId, dispatch, searchParams];
    $[44] = dispatch;
    $[45] = leadId;
    $[46] = searchParams;
    $[47] = t15;
    $[48] = t16;
  } else {
    t15 = $[47];
    t16 = $[48];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[49] !== step) {
    t17 = () => {
      if (step !== "success") {
        return;
      }
      limparSessaoSelf();
    };
    t18 = [step];
    $[49] = step;
    $[50] = t17;
    $[51] = t18;
  } else {
    t17 = $[50];
    t18 = $[51];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[52] !== buscouContexto || $[53] !== dispatch || $[54] !== leadId || $[55] !== objetivo || $[56] !== restauracaoSessaoConcluida || $[57] !== step) {
    t19 = () => {
      if (!STEPS_QUE_EXIGEM_CONFIGURACAO.includes(step)) {
        return;
      }
      if (!restauracaoSessaoConcluida) {
        return;
      }
      const configuracaoMinima = Boolean(objetivo && leadId);
      const etapaRestauravel = STEPS_RESTAURAVEIS_POR_SESSAO.includes(step);
      if (configuracaoMinima && (etapaRestauravel || buscouContexto)) {
        return;
      }
      dispatch(setStep("landing"));
    };
    t20 = [step, objetivo, leadId, buscouContexto, restauracaoSessaoConcluida, dispatch];
    $[52] = buscouContexto;
    $[53] = dispatch;
    $[54] = leadId;
    $[55] = objetivo;
    $[56] = restauracaoSessaoConcluida;
    $[57] = step;
    $[58] = t19;
    $[59] = t20;
  } else {
    t19 = $[58];
    t20 = $[59];
  }
  useEffect(t19, t20);
  let t21;
  if ($[60] !== dispatch) {
    t21 = destino => dispatch(setStep(destino));
    $[60] = dispatch;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const goTo = t21;
  let t22;
  if ($[62] !== dispatch) {
    t22 = valor => dispatch(setObjetivo(valor));
    $[62] = dispatch;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const selecionarObjetivo = t22;
  let t23;
  if ($[64] !== dispatch) {
    t23 = (campo, valor_0) => dispatch(setPerfilCampo({
      campo,
      valor: campo === "cnpj" ? applyCnpjMask(valor_0) : valor_0
    }));
    $[64] = dispatch;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  const atualizarPerfil = t23;
  let t24;
  if ($[66] !== dispatch) {
    t24 = documento => {
      consultaCnpjPromiseRef.current?.abort();
      consultaCnpjPromiseRef.current = dispatch(consultarCnpjNegociacaoSelf(documento));
    };
    $[66] = dispatch;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  const dispararConsultaCnpj = t24;
  let t25;
  if ($[68] !== cnpjConsultado || $[69] !== dispararConsultaCnpj || $[70] !== perfil.cnpj) {
    t25 = () => {
      const documento_0 = normalizeCpfCnpj(perfil.cnpj);
      if (!cnpjPerfilValido(documento_0)) {
        return;
      }
      if (documento_0 === cnpjConsultado) {
        return;
      }
      dispararConsultaCnpj(documento_0);
    };
    $[68] = cnpjConsultado;
    $[69] = dispararConsultaCnpj;
    $[70] = perfil.cnpj;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  const consultarCnpjEmpresa = t25;
  let t26;
  if ($[72] !== dispararConsultaCnpj || $[73] !== perfil.cnpj) {
    t26 = () => {
      const documento_1 = normalizeCpfCnpj(perfil.cnpj);
      if (!cnpjPerfilValido(documento_1)) {
        return;
      }
      dispararConsultaCnpj(documento_1);
    };
    $[72] = dispararConsultaCnpj;
    $[73] = perfil.cnpj;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  const reconsultarCnpjEmpresa = t26;
  let t27;
  if ($[75] !== dispatch || $[76] !== podeBuscar) {
    t27 = () => {
      if (!podeBuscar) {
        dispatch(marcarTentouBuscar());
        return;
      }
      dispatch(buscarContextoEmpresaSelf());
    };
    $[75] = dispatch;
    $[76] = podeBuscar;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  const buscarContexto = t27;
  let t28;
  if ($[78] !== dispatch) {
    t28 = () => dispatch(editarDadosPerfil());
    $[78] = dispatch;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  const editarDados = t28;
  let t29;
  if ($[80] !== dispatch) {
    t29 = () => {
      criarAgentePromiseRef.current = dispatch(criarAgenteSelf());
    };
    $[80] = dispatch;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  const criarAgente = t29;
  let t30;
  if ($[82] !== dispatch || $[83] !== objetivoSemParametros) {
    t30 = () => {
      if (objetivoSemParametros) {
        criarAgentePromiseRef.current = dispatch(criarAgenteSelf());
        return;
      }
      dispatch(setStep("parametros"));
    };
    $[82] = dispatch;
    $[83] = objetivoSemParametros;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  const avancarDoPerfil = t30;
  let t31;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => {
      criarAgentePromiseRef.current?.abort();
    };
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  const cancelarCriacaoAgente = t31;
  let t32;
  if ($[86] !== dispatch) {
    t32 = (key, valor_1) => dispatch(setValorParametro({
      key,
      valor: valor_1
    }));
    $[86] = dispatch;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  const definirValorParametro = t32;
  let t33;
  if ($[88] !== dispatch) {
    t33 = () => dispatch(carregarObjetivosSelf());
    $[88] = dispatch;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  const recarregarObjetivos = t33;
  let t34;
  if ($[90] !== dispatch || $[91] !== objetivo) {
    t34 = () => {
      if (objetivo) {
        dispatch(carregarCamposObjetivoSelf(objetivo));
      }
    };
    $[90] = dispatch;
    $[91] = objetivo;
    $[92] = t34;
  } else {
    t34 = $[92];
  }
  const recarregarCampos = t34;
  let t35;
  if ($[93] !== dispatch) {
    t35 = () => dispatch(buscarContextoEmpresaSelf());
    $[93] = dispatch;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  const tentarBuscaNovamente = t35;
  let t36;
  if ($[95] !== dispatch) {
    t36 = valor_2 => dispatch(setPlanoPeriodo(valor_2));
    $[95] = dispatch;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  const definirPeriodo = t36;
  let t37;
  if ($[97] !== dispatch) {
    t37 = valor_3 => dispatch(setPlanoTier(valor_3));
    $[97] = dispatch;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  const definirTier = t37;
  let t38;
  if ($[99] !== dispatch || $[100] !== termosAceitos) {
    t38 = () => dispatch(setTermosAceitos(!termosAceitos));
    $[99] = dispatch;
    $[100] = termosAceitos;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  const alternarTermos = t38;
  let t39;
  if ($[102] !== dispatch) {
    t39 = tier => {
      dispatch(setPlanoTier(tier));
      const checkoutTab = window.open("", "_blank");
      if (checkoutTab) {
        checkoutTab.opener = null;
        limparSessaoSelfDaAba(checkoutTab);
      }
      dispatch(iniciarPagamentoSelf(tier)).unwrap().then(t40 => {
        const {
          invoiceUrl: url,
          status
        } = t40;
        if (status !== "PENDENTE") {
          checkoutTab?.close();
          return;
        }
        if (!urlPagamentoSegura(url)) {
          checkoutTab?.close();
          reportError(new Error("Link de pagamento recusado pelo guard de URL"), {
            source: "negociacaoSelf.abrirCheckout",
            temUrl: Boolean(url)
          });
          return;
        }
        if (checkoutTab) {
          checkoutTab.location.href = url;
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      }).catch(() => checkoutTab?.close());
    };
    $[102] = dispatch;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  const abrirCheckout = t39;
  let t40;
  if ($[104] !== abrirCheckout || $[105] !== termosAceitos) {
    t40 = tier_0 => {
      if (!termosAceitos) {
        return;
      }
      abrirCheckout(tier_0);
    };
    $[104] = abrirCheckout;
    $[105] = termosAceitos;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  const seguirPagamento = t40;
  let t41;
  if ($[107] !== abrirCheckout || $[108] !== dispatch || $[109] !== planoTier || $[110] !== termosAceitos) {
    t41 = () => {
      if (!termosAceitos) {
        dispatch(setStep("pagamento"));
        return;
      }
      abrirCheckout(planoTier);
    };
    $[107] = abrirCheckout;
    $[108] = dispatch;
    $[109] = planoTier;
    $[110] = termosAceitos;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  const tentarPagamentoNovamente = t41;
  let t42;
  if ($[112] !== dispatch) {
    t42 = () => dispatch(consultarPagamentoSelf());
    $[112] = dispatch;
    $[113] = t42;
  } else {
    t42 = $[113];
  }
  const verificarPagamentoAgora = t42;
  let t43;
  if ($[114] !== dispatch) {
    t43 = valor_4 => dispatch(setChatInput(valor_4));
    $[114] = dispatch;
    $[115] = t43;
  } else {
    t43 = $[115];
  }
  const definirChatInput = t43;
  let t44;
  if ($[116] !== dispatch || $[117] !== lexiDigitando) {
    t44 = texto => {
      const limpo = texto.trim();
      if (!limpo || lexiDigitando) {
        return;
      }
      dispatch(adicionarMensagemUsuario(limpo));
      dispatch(enviarMensagemSimulacaoSelf(limpo));
    };
    $[116] = dispatch;
    $[117] = lexiDigitando;
    $[118] = t44;
  } else {
    t44 = $[118];
  }
  const enviarMensagem = t44;
  let t45;
  if ($[119] !== dispatch) {
    t45 = () => {
      limparSessaoSelf();
      dispatch(reiniciar());
    };
    $[119] = dispatch;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  const reiniciarFluxo = t45;
  let t46;
  if ($[121] !== alternarTermos || $[122] !== atualizarPerfil || $[123] !== avancarDoPerfil || $[124] !== buscandoContexto || $[125] !== buscarContexto || $[126] !== buscouContexto || $[127] !== camposError || $[128] !== camposObjetivo || $[129] !== carregandoCampos || $[130] !== carregandoObjetivos || $[131] !== chatInput || $[132] !== chatMensagens || $[133] !== cnpjEncontrado || $[134] !== cnpjError || $[135] !== consultandoCnpj || $[136] !== consultarCnpjEmpresa || $[137] !== contextoEncontrado || $[138] !== contextoError || $[139] !== criandoAgente || $[140] !== criarAgente || $[141] !== criarAgenteError || $[142] !== definirChatInput || $[143] !== definirPeriodo || $[144] !== definirTier || $[145] !== definirValorParametro || $[146] !== editarDados || $[147] !== emailValido || $[148] !== enviarMensagem || $[149] !== goTo || $[150] !== iniciandoPagamento || $[151] !== invoiceUrl || $[152] !== lexiDigitando || $[153] !== objetivo || $[154] !== objetivoLabel || $[155] !== objetivosCatalogo || $[156] !== objetivosError || $[157] !== pagamentoError || $[158] !== perfil || $[159] !== planoPeriodo || $[160] !== planoResumoLabel || $[161] !== planoTier || $[162] !== podeAvancarParametrosStep || $[163] !== podeAvancarPerfil || $[164] !== podeBuscar || $[165] !== pollingPagamentoExpirado || $[166] !== recarregarCampos || $[167] !== recarregarObjetivos || $[168] !== reconsultarCnpjEmpresa || $[169] !== reiniciarFluxo || $[170] !== seguirPagamento || $[171] !== selecionarObjetivo || $[172] !== showCnpjError || $[173] !== showEmailError || $[174] !== showNomeError || $[175] !== showNomeFantasiaError || $[176] !== showRazaoSocialError || $[177] !== showSiteError || $[178] !== step || $[179] !== stepAnteriorRevisao || $[180] !== tentarBuscaNovamente || $[181] !== tentarPagamentoNovamente || $[182] !== termosAceitos || $[183] !== valoresParametros || $[184] !== verificarPagamentoAgora || $[185] !== wizardSteps) {
    t46 = {
      step,
      objetivo,
      perfil,
      objetivosCatalogo,
      carregandoObjetivos,
      objetivosError,
      buscandoContexto,
      buscouContexto,
      contextoEncontrado,
      contextoError,
      consultandoCnpj,
      cnpjEncontrado,
      cnpjError,
      camposObjetivo,
      carregandoCampos,
      camposError,
      valoresParametros,
      criandoAgente,
      criarAgenteError,
      planoPeriodo,
      planoTier,
      termosAceitos,
      chatMensagens,
      chatInput,
      lexiDigitando,
      invoiceUrl,
      iniciandoPagamento,
      pagamentoError,
      pollingPagamentoExpirado,
      emailValido,
      showNomeError,
      showEmailError,
      showCnpjError,
      showRazaoSocialError,
      showNomeFantasiaError,
      showSiteError,
      podeBuscar,
      podeAvancarPerfil,
      podeAvancarParametrosStep,
      wizardSteps,
      objetivoLabel,
      stepAnteriorRevisao,
      planoResumoLabel,
      goTo,
      avancarDoPerfil,
      selecionarObjetivo,
      atualizarPerfil,
      consultarCnpjEmpresa,
      reconsultarCnpjEmpresa,
      buscarContexto,
      editarDados,
      criarAgente,
      cancelarCriacaoAgente,
      definirValorParametro,
      recarregarObjetivos,
      recarregarCampos,
      tentarBuscaNovamente,
      definirPeriodo,
      definirTier,
      alternarTermos,
      seguirPagamento,
      tentarPagamentoNovamente,
      verificarPagamentoAgora,
      definirChatInput,
      enviarMensagem,
      reiniciarFluxo
    };
    $[121] = alternarTermos;
    $[122] = atualizarPerfil;
    $[123] = avancarDoPerfil;
    $[124] = buscandoContexto;
    $[125] = buscarContexto;
    $[126] = buscouContexto;
    $[127] = camposError;
    $[128] = camposObjetivo;
    $[129] = carregandoCampos;
    $[130] = carregandoObjetivos;
    $[131] = chatInput;
    $[132] = chatMensagens;
    $[133] = cnpjEncontrado;
    $[134] = cnpjError;
    $[135] = consultandoCnpj;
    $[136] = consultarCnpjEmpresa;
    $[137] = contextoEncontrado;
    $[138] = contextoError;
    $[139] = criandoAgente;
    $[140] = criarAgente;
    $[141] = criarAgenteError;
    $[142] = definirChatInput;
    $[143] = definirPeriodo;
    $[144] = definirTier;
    $[145] = definirValorParametro;
    $[146] = editarDados;
    $[147] = emailValido;
    $[148] = enviarMensagem;
    $[149] = goTo;
    $[150] = iniciandoPagamento;
    $[151] = invoiceUrl;
    $[152] = lexiDigitando;
    $[153] = objetivo;
    $[154] = objetivoLabel;
    $[155] = objetivosCatalogo;
    $[156] = objetivosError;
    $[157] = pagamentoError;
    $[158] = perfil;
    $[159] = planoPeriodo;
    $[160] = planoResumoLabel;
    $[161] = planoTier;
    $[162] = podeAvancarParametrosStep;
    $[163] = podeAvancarPerfil;
    $[164] = podeBuscar;
    $[165] = pollingPagamentoExpirado;
    $[166] = recarregarCampos;
    $[167] = recarregarObjetivos;
    $[168] = reconsultarCnpjEmpresa;
    $[169] = reiniciarFluxo;
    $[170] = seguirPagamento;
    $[171] = selecionarObjetivo;
    $[172] = showCnpjError;
    $[173] = showEmailError;
    $[174] = showNomeError;
    $[175] = showNomeFantasiaError;
    $[176] = showRazaoSocialError;
    $[177] = showSiteError;
    $[178] = step;
    $[179] = stepAnteriorRevisao;
    $[180] = tentarBuscaNovamente;
    $[181] = tentarPagamentoNovamente;
    $[182] = termosAceitos;
    $[183] = valoresParametros;
    $[184] = verificarPagamentoAgora;
    $[185] = wizardSteps;
    $[186] = t46;
  } else {
    t46 = $[186];
  }
  return t46;
};
export type UseNegociacaoSelfReturn = ReturnType<typeof useNegociacaoSelf>;